import React, { useCallback, useMemo } from 'react';

import { cssMap, cx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';

import type { LuminaContextType } from '../../analytics/generated/analytics.types';
import type { CardVariant } from '../../knowledge-smart-card/knowledge-smart-card';
import { AnimatedHeader } from '../animated/animated-header';
import { elementRenderers } from '../element/element-renderer-map';
import type { ElementPosition, ElementRendererProps } from '../element/types';
import type { LuminaSourceClickHandler } from '../on-source-click';
import {
	type HeaderElementType,
	isHeaderElement,
	type ParsedElementType,
	type ParsedSubAgentDataMap,
	type SectionElementType,
} from '../types';
import { getLuminaViewComponentType } from '../view-component-type';
import { LuminaViewTracker } from '../view-tracker';
import { layoutRenderers } from './layout-renderer-map';
import type { LayoutRendererProps, LayoutType } from './types';

const styles = cssMap({
	overviewStyle: {
		borderRadius: token('radius.large'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		backgroundColor: token('elevation.surface.sunken'),
		marginBottom: token('space.200'),
	},
	sectionWrapperStyles: {
		marginTop: token('space.300'),
	},
});

export const LuminaSection: React.MemoExoticComponent<
	({
		section,
		subAgentDataMap,
		defaultFoldedState,
		isStreaming,
		smartLinkRenderer,
		cloudId,
		orgId,
		requestingUserId,
		sourceProduct,
		source,
		onSourceClick,
		sessionId,
	}: {
		cloudId: string;
		defaultFoldedState: 'collapsed' | 'expanded' | undefined;
		isStreaming?: boolean;
		onSourceClick?: LuminaSourceClickHandler;
		orgId: string;
		requestingUserId: string;
		section: SectionElementType;
		sessionId?: string;
		smartLinkRenderer?: 'embed';
		source: LuminaContextType['source'];
		sourceProduct?: string;
		subAgentDataMap: ParsedSubAgentDataMap;
	}) => React.JSX.Element | null
> = React.memo(
	({
		section,
		subAgentDataMap,
		defaultFoldedState,
		isStreaming = false,
		smartLinkRenderer,
		source,
		cloudId,
		orgId,
		requestingUserId,
		sourceProduct,
		onSourceClick,
		sessionId,
	}: {
		cloudId: string;
		defaultFoldedState: 'collapsed' | 'expanded' | undefined;
		isStreaming?: boolean;
		onSourceClick?: LuminaSourceClickHandler;
		orgId: string;
		requestingUserId: string;
		section: SectionElementType;
		sessionId?: string;
		smartLinkRenderer?: 'embed';
		source: LuminaContextType['source'];
		sourceProduct?: string;
		subAgentDataMap: ParsedSubAgentDataMap;
	}): React.JSX.Element | null => {
		const sectionKey = section.attributes?.n;

		const layout = section.attributes?.layout || 'one-column';
		const isOverview = section.attributes?.overview === '1';
		const isFoldable = section.attributes?.folded === '1';

		// Wrapper function to render elements using the map
		const renderElement = useCallback(
			(options: {
				component: ParsedElementType;
				isFoldable?: boolean;
				position: ElementPosition;
				variant?: CardVariant;
			}): React.JSX.Element | null => {
				if (typeof options.component === 'string' || !options.component.tag) {
					return null;
				}

				const props: ElementRendererProps = {
					position: options.position,
					variant: options.variant,
					isFoldable: options.isFoldable ?? false,
					isStreaming,
					subAgentDataMap,
					sectionKey,
					smartLinkRenderer,
					cloudId,
					orgId,
					requestingUserId,
					sourceProduct,
					source,
					onSourceClick,
					sessionId,
				};
				const ElementComponent =
					elementRenderers[options.component.tag as keyof typeof elementRenderers];
				if (!ElementComponent) {
					return null;
				}

				const renderedElement = <ElementComponent component={options.component} {...props} />;
				if (!fg('rovo_chat_lumina_component_dwell_tracking')) {
					return renderedElement;
				}
				const componentType = getLuminaViewComponentType(options.component.tag, smartLinkRenderer);

				return componentType ? (
					<LuminaViewTracker componentType={componentType}>{renderedElement}</LuminaViewTracker>
				) : (
					renderedElement
				);
			},
			[
				isStreaming,
				subAgentDataMap,
				sectionKey,
				smartLinkRenderer,
				cloudId,
				orgId,
				requestingUserId,
				sourceProduct,
				source,
				onSourceClick,
				sessionId,
			],
		);

		// Memoize content and header extraction to prevent unnecessary re-renders
		// When parser reuses section objects, section.children will have stable reference
		const content = useMemo(() => {
			if (!Array.isArray(section.children)) {
				return undefined;
			}
			return section.children.filter((element) => !isHeaderElement(element));
		}, [section.children]);

		const header = useMemo(() => {
			if (!Array.isArray(section.children)) {
				return undefined;
			}
			const headerElements = section.children.filter((element) => isHeaderElement(element));
			return headerElements.length ? (headerElements[0] as HeaderElementType) : undefined;
		}, [section.children]);

		if (!content || (content && !content.length)) {
			return null;
		}

		// Create props for layout renderer
		const layoutProps: LayoutRendererProps = {
			content,
			isFoldable,
			isStreaming,
			sectionKey,
			subAgentDataMap,
			renderElement,
			header,
			defaultFoldedState,
		};

		// Determine layout type (add -foldable suffix if needed, but never for overview)
		const layoutType: LayoutType =
			isFoldable && layout !== 'overview' ? `${layout}-foldable` : layout;

		// Render layout using the map
		const renderContent = () => {
			const LayoutComponent = layoutRenderers[layoutType];
			return LayoutComponent ? <LayoutComponent {...layoutProps} /> : null;
		};

		return (
			<Stack
				space="space.100"
				alignInline="start"
				xcss={cx(isOverview && styles.overviewStyle)}
				testId="lumina-layout-section"
				data-layout={layout}
				data-overview={isOverview}
			>
				{/* Render header here only for non-foldable sections.
			    	Foldable sections render their headers inside FoldableWrapper for proper collapsible behavior. */}
				{header && header.attributes?.show !== 'false' && content && !isFoldable && (
					<Inline space="space.100" alignBlock="center" xcss={styles.sectionWrapperStyles}>
						<AnimatedHeader isStreaming={isStreaming}>{header.children as string}</AnimatedHeader>
					</Inline>
				)}
				{renderContent()}
			</Stack>
		);
	},
	(prevProps, nextProps) => {
		// Only re-render if props actually changed
		return (
			prevProps.section === nextProps.section &&
			prevProps.subAgentDataMap === nextProps.subAgentDataMap &&
			prevProps.defaultFoldedState === nextProps.defaultFoldedState &&
			prevProps.isStreaming === nextProps.isStreaming &&
			prevProps.smartLinkRenderer === nextProps.smartLinkRenderer &&
			prevProps.cloudId === nextProps.cloudId &&
			prevProps.orgId === nextProps.orgId &&
			prevProps.requestingUserId === nextProps.requestingUserId &&
			prevProps.sourceProduct === nextProps.sourceProduct &&
			prevProps.onSourceClick === nextProps.onSourceClick &&
			prevProps.sessionId === nextProps.sessionId
		);
	},
);
